import React, { useEffect, useState, MouseEvent } from 'react';
import { Container, Heading, Box, Image, Table, Thead, Tbody, Tr, Th, Td, Badge, Tag, Spinner, Center, Tooltip, Input, SimpleGrid, FormControl, FormLabel, Alert, AlertIcon, AlertTitle, AlertDescription, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { TriangleUpIcon, TriangleDownIcon } from '@chakra-ui/icons';
import { TableHeader } from '../components/shared/TableHeader';

// Add cache interface and variables
interface CacheData<T> {
  data: T[];
  timestamp: number;
}

// Cache for racers data - defined outside component to persist between renders
let racersCache: CacheData<RacerSummary> | null = null;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

// Update GoldRecord interface to match the new view structure
interface GoldRecord {
  gold_name: string;
  gold_year: string | null;
  gold_team: string | null;
  gold_country: string | null;
  gold_reg_num_zsl: string | null;
  match_name_cluster_id: number;
  match_name_normalized: string;
  match_golden_record: boolean;
  best_position: number;
  best_time: string | null;
  last_race: string | null;
  num_races: number;
}

interface RacerSummary {
  name: string;
  team: string | null;
  best_position: number;
  race_category: string;
  year: number | null;
  best_time: string | null;
  last_race: string | null;
  num_races: number;
}

interface SortConfig {
  key: keyof RacerSummary | null;
  direction: 'asc' | 'desc' | null;
}

export default function Racers() {
  const navigate = useNavigate();
  const [racers, setRacers] = useState<RacerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<'timeout' | 'error' | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });
  const [maxRaces, setMaxRaces] = useState<number>(0);
  const [filters, setFilters] = useState({
    name: '',
    country: '',
    team: '',
    year: ''
  });
  // Increment to retry fetch after timeout or error
  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    const fetchRacers = async () => {
      try {
        // Check if we have cached data that's still valid
        const now = Date.now();
        if (racersCache && (now - racersCache.timestamp < CACHE_DURATION)) {
          console.log('Using cached racers data');
          setRacers(racersCache.data);
          setMaxRaces(Math.max(...racersCache.data.map(r => r.num_races)));
          setIsLoading(false);
          setFetchError(null);
          return;
        }

        setFetchError(null);
        console.log('Fetching fresh racers data from Supabase');
        const { data, error } = await supabase
          .from('frontend_racers_summary_mv')
          .select('*')
          .order('gold_name');

        if (error) {
          // 57014 = statement timeout; view query too slow for Supabase default
          console.error('Error fetching racers:', error.code, error.message, error);
          setFetchError(error.code === '57014' ? 'timeout' : 'error');
          return;
        }

        if (data) {
          const typedData = data as GoldRecord[];

          // Find max number of races
          const maxNumRaces = Math.max(...typedData.map(r => r.num_races));
          setMaxRaces(maxNumRaces);

          const racerSummaries = typedData.map((record: GoldRecord) => ({
            name: record.gold_name,
            team: record.gold_team,
            race_category: record.gold_country || '',
            best_position: record.best_position,
            year: record.gold_year ? parseInt(record.gold_year) : null,
            best_time: record.best_time,
            last_race: record.last_race,
            num_races: record.num_races
          }));

          // Update the cache with fresh data
          racersCache = {
            data: racerSummaries,
            timestamp: now
          };

          setRacers(racerSummaries);
        }
        setFetchError(null);
        setIsLoading(false);
      } catch (e) {
        console.error('Exception in fetchRacers:', e);
        setFetchError('error');
        setIsLoading(false);
      }
    };

    fetchRacers();
  }, [fetchTrigger]);

  const handleSort = (key: keyof RacerSummary) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortData = (data: RacerSummary[]): RacerSummary[] => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];
      
      // Special handling for null/undefined values
      if (aValue === null || aValue === undefined) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return sortConfig.direction === 'desc' ? 1 : -1;
      }

      // Special handling for best_position
      if (sortConfig.key === 'best_position') {
        const aPos = aValue as number;
        const bPos = bValue as number;
        
        // Handle DNF (0) positions
        if (aPos === 0 && bPos === 0) return 0;
        if (aPos === 0) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bPos === 0) return sortConfig.direction === 'asc' ? -1 : 1;
        
        return sortConfig.direction === 'asc' ? aPos - bPos : bPos - aPos;
      }

      // Default string comparison
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  const isNewRacer = (numRaces: number, lastRaceDate: string | null): boolean => {
    if (numRaces !== 1 || !lastRaceDate) return false;
    
    const lastRace = new Date(lastRaceDate);
    const fiveMonthsAgo = new Date();
    fiveMonthsAgo.setMonth(fiveMonthsAgo.getMonth() - 5);
    
    return lastRace >= fiveMonthsAgo;
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filterRacers = (racers: RacerSummary[]): RacerSummary[] => {
    return racers.filter(racer => {
      const nameMatch = racer.name.toLowerCase().includes(filters.name.toLowerCase());
      const countryMatch = racer.race_category.toLowerCase().includes(filters.country.toLowerCase());
      const teamMatch = (racer.team || '').toLowerCase().includes(filters.team.toLowerCase());
      const yearMatch = racer.year?.toString().includes(filters.year) || false;
      
      return nameMatch && countryMatch && teamMatch && yearMatch;
    });
  };

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="50vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Center>
      </Container>
    );
  }

  // Show error state with retry when view fetch failed (e.g. statement timeout 57014)
  if (fetchError) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box mb={8} position="relative">
          <Image
            src="/img/racers.jpeg"
            alt="Racers"
            width="100%"
            height="300px"
            objectFit="cover"
            objectPosition="top"
            borderRadius="lg"
            fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
          />
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={6}
            background="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))"
            borderBottomRadius="lg"
          >
            <Heading color="white">All racers</Heading>
          </Box>
        </Box>
        <Alert status="warning" variant="subtle" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize="sm">
              {fetchError === 'timeout' ? 'Request timed out' : 'Failed to load racers'}
            </AlertTitle>
            <AlertDescription fontSize="sm">
              {fetchError === 'timeout'
                ? 'The racer summary view is slow and hit the server timeout. Try again or increase statement_timeout in Supabase (see data/_SQL/fix_racers_summary_timeout.sql).'
                : 'Could not load racer list. Try again.'}
            </AlertDescription>
          </Box>
          <Button size="sm" colorScheme="blue" onClick={() => setFetchTrigger(t => t + 1)}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header Image - Updated objectPosition */}
      <Box mb={8} position="relative">
        <Image
          src="/img/racers.jpeg"
          alt="Racers"
          width="100%"
          height="300px"
          objectFit="cover"
          objectPosition="top"
          borderRadius="lg"
          fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={6}
          background="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))"
          borderBottomRadius="lg"
        >
          <Heading color="white">All racers</Heading>
        </Box>
      </Box>

      {/* Racers Table – size="sm" on mobile for compact rows */}
      <Box overflowX="auto" shadow="md" borderRadius="lg">
        <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
          <Thead bg="gray.50">
            <Tr>
              <TableHeader<RacerSummary> 
                label="Name" 
                sortKey="name" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Country" 
                sortKey="race_category" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Team" 
                sortKey="team" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Year Born" 
                sortKey="year" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Best Position" 
                sortKey="best_position" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Best Time" 
                sortKey="best_time" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
              <TableHeader<RacerSummary> 
                label="Last Race" 
                sortKey="last_race" 
                sortConfig={sortConfig} 
                onSort={handleSort} 
              />
            </Tr>
            {/* Filter row – responsive padding for mobile compactness */}
            <Tr>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}>
                <FormControl>
                  <Input
                    id="filter-name"
                    name="filter-name"
                    placeholder="Filter by name"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    size={{ base: 'xs', md: 'md' }}
                  />
                </FormControl>
              </Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}>
                <FormControl>
                  <Input
                    id="filter-country"
                    name="filter-country"
                    placeholder="Filter by country"
                    value={filters.country}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    size={{ base: 'xs', md: 'md' }}
                  />
                </FormControl>
              </Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}>
                <FormControl>
                  <Input
                    id="filter-team"
                    name="filter-team"
                    placeholder="Filter by team"
                    value={filters.team}
                    onChange={(e) => handleFilterChange('team', e.target.value)}
                    size={{ base: 'xs', md: 'md' }}
                  />
                </FormControl>
              </Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}>
                <FormControl>
                  <Input
                    id="filter-year"
                    name="filter-year"
                    placeholder="Filter by year"
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    size={{ base: 'xs', md: 'md' }}
                  />
                </FormControl>
              </Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}></Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}></Th>
              <Th px={{ base: 1, md: 4 }} py={{ base: 1, md: 2 }}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {filterRacers(sortData(racers)).map((racer) => (
              <Tr 
                key={racer.name}
                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                onClick={() => navigate(`/racer/${encodeURIComponent(racer.name)}`)}
              >
                <Td fontWeight="medium">
                  <Box display="flex" alignItems="center" gap={2}>
                    {racer.name}
                    {isNewRacer(racer.num_races, racer.last_race) && (
                      <Tooltip label="Nový jazdec" hasArrow>
                        <Tag size="sm" colorScheme="green" variant="subtle" fontSize="xs">
                          New
                        </Tag>
                      </Tooltip>
                    )}
                    {racer.num_races === maxRaces && (
                      <Tooltip label="Jazdec s najvyšším počtom štartov" hasArrow>
                        <Tag size="sm" colorScheme="purple" variant="subtle" fontSize="xs">
                          Top
                        </Tag>
                      </Tooltip>
                    )}
                  </Box>
                </Td>
                <Td>{racer.race_category}</Td>
                {/* Team cell – truncated with ellipsis to prevent multi-line row inflation */}
                <Td 
                  style={{ 
                    padding: '8px 10px', 
                    borderBottom: '1px solid #E2E8F0', 
                    cursor: racer.team ? 'pointer' : 'default',
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={racer.team || ''}
                  onClick={() => racer.team && navigate(`/teamDetail/${encodeURIComponent(racer.team)}`)}
                  onMouseEnter={(e) => racer.team && (e.currentTarget.style.color = 'darkblue')}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                >
                  {racer.team || '-'}
                </Td>
                <Td isNumeric>{racer.year || '-'}</Td>
                <Td isNumeric>{racer.best_position === 0 ? 'DNF' : racer.best_position}</Td>
                <Td isNumeric>{racer.best_time || '-'}</Td>
                <Td>{racer.last_race ? new Date(racer.last_race).toLocaleDateString() : '-'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Container>
  );
} 